/**
 * Every pull request the reader may see, for the reviews page and the rail's
 * held count: one `GET /api/code-review/prs` per query (the search, the chips,
 * and each section's page). The server pages both sections and applies the
 * reader's Team visibility; this hook adds which rows sit on the catch-all
 * Reviews team, read from the worker directory every surface already shares
 * (`useWorkerDirectoryEntries`).
 *
 * The rail and the page read one module-level result per query, kept thirty
 * seconds and dropped the moment a gate moves (`tickets.invalidate`,
 * `work_item.updated`) or a write on the page calls `invalidateReviews`.
 */
import { useEffect, useMemo, useState } from "react";
import type { CodeReviewListResponse, CodeReviewListSection } from "@yagni/shared";
import { CODE_REVIEW_SLUG } from "../workers/crafts.js";
import { useWorkerDirectoryEntries } from "../work/useWorkerLookup.js";
import { reviewsApi, type ReviewsApi, type ReviewsQuery } from "./reviewsApi.js";
import { captureUiError } from "../sentry.js";
import {
  reviewingTeamsOf,
  startOfDay,
  type ReviewingTeams,
  type ReviewRow,
} from "./reviewsModel.js";

const TTL_MS = 30_000;

export const DEFAULT_REVIEWS_QUERY: ReviewsQuery = {
  q: "",
  status: "",
  repo: "",
  author: "",
  page: 1,
  heldPage: 1,
};

let cache = new Map<string, { at: number; promise: Promise<CodeReviewListResponse> }>();
const listeners = new Set<() => void>();

/** Drop every cached listing and tell each mounted reader to read again. */
export function invalidateReviews(): void {
  cache = new Map();
  for (const listener of listeners) listener();
}

/** Test-only. */
export function __resetReviewsCacheForTests(): void {
  cache = new Map();
}

if (typeof window !== "undefined") {
  window.addEventListener("tickets.invalidate", invalidateReviews);
  window.addEventListener("work_item.updated", invalidateReviews);
}

/** The reader's own midnight, as the server's "reviews today" boundary. */
export function sinceMidnight(now = Date.now()): string {
  return new Date(startOfDay(now)).toISOString();
}

/**
 * Why a listing body is not one, in words safe to send to Sentry (its shape,
 * never its contents), or null when it has both sections.
 */
export function malformedListing(body: unknown): string | null {
  const record = body && typeof body === "object" ? (body as Record<string, unknown>) : null;
  if (record?.held && record.rest) return null;
  const kind = Array.isArray(body) ? "array" : body === null ? "null" : typeof body;
  const has = (key: string) => (record?.[key] ? "yes" : "no");
  return `The reviews listing answered without its sections (${kind}, held: ${has("held")}, rest: ${has("rest")})`;
}

/** One listing, through the shared cache. */
export function readReviews(api: ReviewsApi, query: ReviewsQuery): Promise<CodeReviewListResponse> {
  const since = sinceMidnight();
  const key = `${JSON.stringify(query)}@${since}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;
  // A body without both sections (a proxy's error page, a stubbed `[]`) is a
  // failed read, not a page that crashes reading `held.total`.
  const promise = api.listReviews(query, since).then((body) => {
    const malformed = malformedListing(body);
    if (malformed) throw new Error(malformed);
    return body;
  });
  cache.set(key, { at: Date.now(), promise });
  // A failed read is not kept: the next reader asks again.
  promise.catch(() => cache.delete(key));
  return promise;
}

export interface ReviewsSection {
  rows: ReviewRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ReviewsList {
  held: ReviewsSection;
  rest: ReviewsSection;
  reviewsToday: number;
  repos: string[];
  authors: string[];
  teams: ReviewingTeams;
  ready: boolean;
  failed: boolean;
  refresh: () => void;
}

function useInvalidationVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const listener = () => setVersion((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return version;
}

function useListing(query: ReviewsQuery, api: ReviewsApi) {
  const version = useInvalidationVersion();
  const key = JSON.stringify(query);
  const [state, setState] = useState<{
    key: string;
    body: CodeReviewListResponse | null;
    failed: boolean;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    readReviews(api, query)
      .then((body) => {
        if (!cancelled) setState({ key, body, failed: false });
      })
      .catch((err: unknown) => {
        captureUiError(err, { area: "Reviews", operation: "read_reviews", severity: "warning" });
        if (!cancelled) setState({ key, body: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
    // `query` is keyed by `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version, api]);
  return state?.key === key ? state : null;
}

const EMPTY_SECTION = (pageSize: number, page: number): ReviewsSection => ({
  rows: [],
  total: 0,
  page,
  pageSize,
});

export function useReviewsList(
  query: ReviewsQuery,
  /** The Teams the reader can see (null while loading), for the catch-all Reviews team. */
  desks: readonly { id: string }[] | null,
  api: ReviewsApi = reviewsApi,
): ReviewsList {
  const directory = useWorkerDirectoryEntries();
  const teams = useMemo(() => {
    const readable = desks ? new Set(desks.map((desk) => desk.id)) : undefined;
    return reviewingTeamsOf(directory.entries, CODE_REVIEW_SLUG, readable);
  }, [directory.entries, desks]);
  const loaded = useListing(query, api);
  const body = loaded?.body ?? undefined;
  const [held, rest] = useMemo(() => {
    const catchAll = new Set(teams.catchAll);
    const section = (
      from: CodeReviewListSection | undefined,
      pageSize: number,
      page: number,
    ): ReviewsSection =>
      from
        ? { ...from, rows: from.rows.map((pr) => ({ ...pr, unowned: catchAll.has(pr.deskId) })) }
        : EMPTY_SECTION(pageSize, page);
    return [section(body?.held, 5, query.heldPage), section(body?.rest, 20, query.page)];
  }, [body, teams.catchAll, query.heldPage, query.page]);
  return {
    held,
    rest,
    reviewsToday: body?.reviewsToday ?? 0,
    repos: body?.repos ?? [],
    authors: body?.authors ?? [],
    teams,
    ready: loaded != null && (directory.ready || directory.failed),
    failed: loaded?.failed ?? false,
    refresh: invalidateReviews,
  };
}

/** The rail's count: every review Proctor holds for this reader, across the Teams they can see. */
export function useHeldReviewCount(api: ReviewsApi = reviewsApi): number {
  return useListing(DEFAULT_REVIEWS_QUERY, api)?.body?.held.total ?? 0;
}
